import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as MoreIcon } from "../../assets/icons/3Dot.svg";
import { ReactComponent as BookmarkIcon } from "../../assets/icons/bookmark.svg";
import { ReactComponent as CitationIcon } from "../../assets/icons/citation.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as FaqIcon } from "../../assets/icons/faq.svg";
import { ReactComponent as LanguageIcon } from "../../assets/icons/language.svg";
import { ReactComponent as PrintIcon } from "../../assets/icons/print.svg";
import { ReactComponent as RedoIcon } from "../../assets/icons/redo.svg";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as ShareIcon } from "../../assets/icons/share.svg";
import { ReactComponent as UndoIcon } from "../../assets/icons/undo.svg";
import useReaderStore from "../../context/readerStore";
import useActionGuard from "../../hooks/useActionGuard";
import { useIsTouchTablet } from "../../hooks/useMediaQuery";
import { LANGUAGE_OPTIONS } from "../../i18n/languages";
import {
  createBookmarkLabel,
  isPageBookmarked,
  toggleBookmark,
} from "../../services/bookmarkService";
import { isFeatureAllowed } from "../../services/drmPolicyService";
import {
  getEpubTotalSections,
  loadEpubDocument,
} from "../../services/epubService";
import { showComingSoonNotification } from "../../services/notificationService";
import { loadPdfDocument } from "../../services/pdfService";
import { getCtrlLabel } from "../../utils/platformUtils";
import DisplayMenu from "./DisplayMenu";
import "./Toolbar.css";

const Toolbar = ({ onToggleHelp }) => {
  const {
    currentPage,
    totalPages,
    scale,
    setScale,
    setCurrentPage,
    documentType,
    setDocument,
    setTotalPages,
    setError,
    setLoading,
    documentName,
    toggleSearchSidebar,
    isSearchSidebarOpen,
    annotations,
    setAnnotationData,
    setAnnotationLoading,
    drmPolicy,
    setReaderViewMode,
  } = useReaderStore();

  const { t, i18n } = useTranslation();

  const displayName = documentName || t("common.document");

  const isTouchTablet = useIsTouchTablet();
  const minScale =
    documentType === "epub"
      ? isTouchTablet
        ? 1.0
        : 0.75
      : isTouchTablet
        ? 1.0
        : 0.5;

  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isDisplayMenuOpen, setIsDisplayMenuOpen] = useState(false);
  const [currentSubmenu, setCurrentSubmenu] = useState(null);
  const moreMenuRef = useRef(null);
  const moreButtonRef = useRef(null);
  const displayMenuButtonRef = useRef(null);
  const lastUndoDispatchRef = useRef(0);
  const lastRedoDispatchRef = useRef(0);
  const undoCooldownTimerRef = useRef(null);
  const redoCooldownTimerRef = useRef(null);
  const UNDO_REDO_DEBOUNCE_MS = 550;
  const COOLDOWN_MS = 550;

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [undoCooldownEnd, setUndoCooldownEnd] = useState(0);
  const [redoCooldownEnd, setRedoCooldownEnd] = useState(0);
  const ctrlLabel = useMemo(() => getCtrlLabel(), []);

  const highlightsAllowed = isFeatureAllowed(drmPolicy, "highlights");
  const underlineAllowed = isFeatureAllowed(drmPolicy, "underline");
  const undoRedoAllowed = highlightsAllowed || underlineAllowed;
  const downloadAllowed = isFeatureAllowed(drmPolicy, "download");
  const citationsAllowed = isFeatureAllowed(drmPolicy, "citations");
  const printingAllowed = isFeatureAllowed(drmPolicy, "printing");

  const toggleMoreMenu = useCallback(() => {
    setIsMoreMenuOpen((v) => !v);
    setCurrentSubmenu(null);
    setIsDisplayMenuOpen(false);
  }, []);

  const toggleDisplayMenu = useCallback(() => {
    setIsDisplayMenuOpen((open) => !open);
    setIsMoreMenuOpen(false);
    setCurrentSubmenu(null);
  }, []);

  const closeDisplayMenu = useCallback(() => {
    setIsDisplayMenuOpen(false);
  }, []);

  const handleOpenSubmenu = useCallback((submenuName) => {
    setCurrentSubmenu(submenuName);
  }, []);

  const handleBackFromSubmenu = useCallback(() => {
    setCurrentSubmenu(null);
  }, []);

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
  };

  // Get bookmarks from store
  const apiBookmarks = annotations?.bookmarks?.data || [];

  const isCurrentBookmarked = useMemo(() => {
    if (!documentType || !currentPage) return false;
    return isPageBookmarked(apiBookmarks, currentPage);
  }, [apiBookmarks, currentPage, documentType]);

  useEffect(() => {
    if (!isMoreMenuOpen || typeof window === "undefined") return;

    const doc = window.document;

    const handleClickOutside = (event) => {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(event.target)
      ) {
        setIsMoreMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMoreMenuOpen(false);
      }
    };

    doc.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleEscape);

    return () => {
      doc.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMoreMenuOpen]);

  useEffect(() => {
    if (!documentType) {
      setIsMoreMenuOpen(false);
    }
  }, [documentType]);

  const executeToggleBookmark = useCallback(async () => {
    if (!documentType || !currentPage) return;
    setAnnotationLoading("bookmarks", true);
    await toggleBookmark({
      pageNumber: currentPage,
      title: createBookmarkLabel(documentType, currentPage),
      currentBookmarks: apiBookmarks,
      onSuccess: (updatedBookmarks) => {
        setAnnotationData("bookmarks", updatedBookmarks);
        setAnnotationLoading("bookmarks", false);
      },
      onError: (error) => {
        console.error("Error toggling bookmark:", error);
        setAnnotationLoading("bookmarks", false);
      },
    });
  }, [
    documentType,
    currentPage,
    apiBookmarks,
    setAnnotationData,
    setAnnotationLoading,
  ]);

  const { run: handleToggleBookmark } = useActionGuard(executeToggleBookmark);

  useEffect(() => {
    const handleCapabilities = (e) => {
      const { canUndo: u, canRedo: r } = e.detail || {};
      setCanUndo(!!u);
      setCanRedo(!!r);
    };
    window.addEventListener("reader:can-undo", handleCapabilities);
    return () => {
      window.removeEventListener("reader:can-undo", handleCapabilities);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (undoCooldownTimerRef.current)
        clearTimeout(undoCooldownTimerRef.current);
      if (redoCooldownTimerRef.current)
        clearTimeout(redoCooldownTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const handleBookmarkToggle = () => {
      handleToggleBookmark();
    };

    window.addEventListener("reader:toggle-bookmark", handleBookmarkToggle);
    return () => {
      window.removeEventListener(
        "reader:toggle-bookmark",
        handleBookmarkToggle,
      );
    };
  }, [handleToggleBookmark]);

  const handleZoomIn = useCallback(() => {
    if (documentType !== "pdf" && documentType !== "epub") return;
    const maxScale = documentType === "epub" ? 2.0 : 5.0;
    const step = documentType === "epub" ? 0.2 : 0.1;
    setScale(Math.min(maxScale, scale + step));
    if (documentType === "pdf") {
      setReaderViewMode("default");
    }
  }, [scale, setScale, setReaderViewMode, documentType]);

  const handleZoomOut = useCallback(() => {
    if (documentType !== "pdf" && documentType !== "epub") return;
    setScale(Math.max(minScale, scale - 0.1));
    if (documentType === "pdf") {
      setReaderViewMode("default");
    }
  }, [scale, setScale, setReaderViewMode, documentType, minScale]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      window.dispatchEvent(
        new CustomEvent("reader:go-to-page", { detail: { page: nextPage } }),
      );
    }
  }, [currentPage, totalPages, setCurrentPage]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      window.dispatchEvent(
        new CustomEvent("reader:go-to-page", { detail: { page: prevPage } }),
      );
    }
  }, [currentPage, setCurrentPage]);

  const dispatchUndo = useCallback(() => {
    if (!undoRedoAllowed) return;
    if (documentType !== "pdf" && documentType !== "epub") return;
    const now = Date.now();
    if (now - lastUndoDispatchRef.current < UNDO_REDO_DEBOUNCE_MS) return;
    if (undoCooldownEnd > 0 || !canUndo) return;
    lastUndoDispatchRef.current = now;
    if (undoCooldownTimerRef.current)
      clearTimeout(undoCooldownTimerRef.current);
    setUndoCooldownEnd(now + COOLDOWN_MS);
    undoCooldownTimerRef.current = setTimeout(
      () => setUndoCooldownEnd(0),
      COOLDOWN_MS,
    );
    window.dispatchEvent(new CustomEvent("reader:undo"));
  }, [undoRedoAllowed, documentType, undoCooldownEnd, canUndo]);

  const dispatchRedo = useCallback(() => {
    if (!undoRedoAllowed) return;
    if (documentType !== "pdf" && documentType !== "epub") return;
    const now = Date.now();
    if (now - lastRedoDispatchRef.current < UNDO_REDO_DEBOUNCE_MS) return;
    if (redoCooldownEnd > 0 || !canRedo) return;
    lastRedoDispatchRef.current = now;
    if (redoCooldownTimerRef.current)
      clearTimeout(redoCooldownTimerRef.current);
    setRedoCooldownEnd(now + COOLDOWN_MS);
    redoCooldownTimerRef.current = setTimeout(
      () => setRedoCooldownEnd(0),
      COOLDOWN_MS,
    );
    window.dispatchEvent(new CustomEvent("reader:redo"));
  }, [undoRedoAllowed, documentType, redoCooldownEnd, canRedo]);

  // Handle file upload
  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setLoading(true);
      try {
        if (file.name.toLowerCase().endsWith(".pdf")) {
          const reader = new FileReader();
          reader.onload = async (ev) => {
            try {
              const pdf = await loadPdfDocument(ev.target.result);
              setDocument(pdf, "pdf", file.name);
              setTotalPages(pdf?.numPages || 0);
            } catch (e) {
              setError(e?.message || t("errors.failedToLoadPdf"));
            } finally {
              setLoading(false);
            }
          };
          reader.readAsArrayBuffer(file);
        } else if (file.name.toLowerCase().endsWith(".epub")) {
          const reader = new FileReader();
          reader.onload = async (ev) => {
            try {
              const epub = await loadEpubDocument(ev.target.result);
              const totalSections = await getEpubTotalSections(epub);
              setDocument(epub, "epub", file.name);
              setTotalPages(totalSections);
            } catch (e) {
              setError(e?.message || t("errors.failedToLoadEpub"));
            } finally {
              setLoading(false);
            }
          };
          reader.readAsArrayBuffer(file);
        } else {
          setError(t("errors.unsupportedFileType"));
          setLoading(false);
        }
      } catch (e) {
        setError(t("errors.errorReadingFile"));
        setLoading(false);
      }
    },
    [setDocument, setTotalPages, setError, setLoading],
  );

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't process shortcuts if user is typing in an input field
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      switch (e.key) {
        case "ArrowRight":
        case "PageDown":
          if (documentType === "epub") return;
          e.preventDefault();
          handleNextPage();
          break;
        case "ArrowLeft":
        case "PageUp":
          if (documentType === "epub") return;
          e.preventDefault();
          handlePrevPage();
          break;
        case "z":
        case "Z":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            dispatchUndo();
          }
          break;
        case "y":
        case "Y":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            dispatchRedo();
          }
          break;
        case "+":
        case "=":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            if (documentType === "pdf" || documentType === "epub")
              handleZoomIn();
          }
          break;
        case "-":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            if (documentType === "pdf" || documentType === "epub")
              handleZoomOut();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    currentPage,
    totalPages,
    documentType,
    handleNextPage,
    handlePrevPage,
    handleZoomIn,
    handleZoomOut,
    dispatchUndo,
    dispatchRedo,
  ]);

  return (
    <div className="toolbar">
      <div className="toolbar-left">
        {documentType && (
          <div className="document-info">
            <div className="document-name" title={displayName}>
              {displayName}
            </div>
            <div className={`document-badge ${documentType}`}>
              {documentType?.toUpperCase()}
            </div>
          </div>
        )}
        <input
          type="file"
          accept=".pdf,.epub"
          id="file-upload-input"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      <div className="toolbar-center"></div>

      <div className="toolbar-right">
        {documentType && (
          <>
            {undoRedoAllowed &&
              (documentType === "pdf" || documentType === "epub") && (
                <>
                  <button
                    className={`icon-button${
                      !canUndo || undoCooldownEnd > 0 ? " is-disabled" : ""
                    }`}
                    onClick={dispatchUndo}
                    title={
                      undoCooldownEnd > 0
                        ? t("floatingToolbar.undoCooldown")
                        : t("floatingToolbar.undo", { ctrl: ctrlLabel })
                    }
                    aria-label={t("floatingToolbar.undo", { ctrl: ctrlLabel })}
                    disabled={!canUndo || undoCooldownEnd > 0}
                  >
                    <UndoIcon width={18} height={18} aria-hidden="true" />
                  </button>
                  <button
                    className={`icon-button${
                      !canRedo || redoCooldownEnd > 0 ? " is-disabled" : ""
                    }`}
                    onClick={dispatchRedo}
                    title={
                      redoCooldownEnd > 0
                        ? t("floatingToolbar.redoCooldown")
                        : t("floatingToolbar.redo", { ctrl: ctrlLabel })
                    }
                    aria-label={t("floatingToolbar.redo", { ctrl: ctrlLabel })}
                    disabled={!canRedo || redoCooldownEnd > 0}
                  >
                    <RedoIcon width={18} height={18} aria-hidden="true" />
                  </button>
                  <span className="toolbar-divider" aria-hidden="true"></span>
                </>
              )}

            {/* Eye / display settings */}
            <div className="toolbar-display-menu">
              <button
                ref={displayMenuButtonRef}
                className={`icon-button${
                  isDisplayMenuOpen ? " is-active" : ""
                }`}
                type="button"
                title={t("toolbar.view")}
                aria-label={t("toolbar.view")}
                aria-haspopup="menu"
                aria-expanded={isDisplayMenuOpen}
                onClick={toggleDisplayMenu}
              >
                <EyeIcon className="icon-v2" aria-hidden="true" />
              </button>
              <DisplayMenu
                isOpen={isDisplayMenuOpen}
                onClose={closeDisplayMenu}
                anchorRef={displayMenuButtonRef}
              />
            </div>

            {isFeatureAllowed(drmPolicy, "bookmarks") && (
              <button
                className={`icon-button bookmark-toggle-button${
                  isCurrentBookmarked ? " is-active" : ""
                }`}
                onClick={() => {
                  handleToggleBookmark();
                }}
                aria-pressed={isCurrentBookmarked}
                aria-label={
                  isCurrentBookmarked
                    ? t("toolbar.removeBookmark")
                    : t("toolbar.addBookmark")
                }
                title={
                  isCurrentBookmarked
                    ? t("toolbar.bookmarked")
                    : t("toolbar.addBookmark")
                }
              >
                <BookmarkIcon width={18} height={18} aria-hidden="true" />
              </button>
            )}

            {isFeatureAllowed(drmPolicy, "search") && (
              <button
                className={`icon-button${
                  isSearchSidebarOpen ? " is-active" : ""
                }`}
                title={t("common.search")}
                aria-label={t("common.search")}
                aria-pressed={isSearchSidebarOpen}
                aria-expanded={isSearchSidebarOpen}
                onClick={() => {
                  toggleSearchSidebar();
                }}
              >
                <SearchIcon width={18} height={18} aria-hidden="true" />
              </button>
            )}

            {/* Share — placeholder, no action yet */}
            <button
              className="icon-button is-coming-soon"
              type="button"
              title={t("toolbar.share")}
              aria-label={t("toolbar.share")}
              onClick={() => showComingSoonNotification("share")}
            >
              <ShareIcon className="icon-v2" aria-hidden="true" />
            </button>

            <div className="toolbar-more">
              <button
                className={`icon-button${isMoreMenuOpen ? " is-active" : ""}`}
                title={t("toolbar.moreOptions")}
                aria-label={t("toolbar.moreOptions")}
                aria-haspopup="true"
                aria-expanded={isMoreMenuOpen}
                onClick={toggleMoreMenu}
                ref={moreButtonRef}
              >
                <MoreIcon width={18} height={18} aria-hidden="true" />
              </button>

              {isMoreMenuOpen && (
                <div className="toolbar-more-menu" ref={moreMenuRef}>
                  {/* Main Menu */}
                  {!currentSubmenu && (
                    <>
                      {/* Language Section */}
                      <div className="menu-section">
                        <button
                          className="toolbar-menu-item"
                          onClick={() => handleOpenSubmenu("language")}
                          type="button"
                        >
                          <span className="menu-item-icon">
                            <LanguageIcon
                              width={20}
                              height={20}
                              aria-hidden="true"
                            />
                          </span>
                          <span className="menu-item-label">
                            {t("common.language")}
                          </span>
                          <span className="menu-item-arrow" aria-hidden="true">
                            ›
                          </span>
                        </button>
                      </div>

                      {/* Placeholder actions — UI only for now */}
                      <div className="menu-section">
                        {downloadAllowed && (
                          <button
                            className="toolbar-menu-item is-coming-soon"
                            type="button"
                            title={t("contextMenu.comingSoon", "Coming soon")}
                            onClick={() =>
                              showComingSoonNotification("download")
                            }
                          >
                            <span className="menu-item-icon">
                              <DownloadIcon
                                width={18}
                                height={18}
                                aria-hidden="true"
                              />
                            </span>
                            <span className="menu-item-label">
                              {t("toolbar.download")}
                            </span>
                          </button>
                        )}
                        {citationsAllowed && (
                          <button
                            className="toolbar-menu-item is-coming-soon"
                            type="button"
                            title={t("contextMenu.comingSoon", "Coming soon")}
                            onClick={() =>
                              showComingSoonNotification("citation")
                            }
                          >
                            <span className="menu-item-icon">
                              <CitationIcon
                                width={18}
                                height={18}
                                aria-hidden="true"
                              />
                            </span>
                            <span className="menu-item-label">
                              {t("toolbar.citation")}
                            </span>
                          </button>
                        )}
                        {printingAllowed && (
                          <button
                            className="toolbar-menu-item is-coming-soon"
                            type="button"
                            title={t("contextMenu.comingSoon", "Coming soon")}
                            onClick={() =>
                              showComingSoonNotification("print")
                            }
                          >
                            <span className="menu-item-icon">
                              <PrintIcon
                                width={18}
                                height={18}
                                aria-hidden="true"
                              />
                            </span>
                            <span className="menu-item-label">
                              {t("toolbar.print")}
                            </span>
                          </button>
                        )}
                        <button
                          className="toolbar-menu-item"
                          type="button"
                          onClick={() => {
                            setIsMoreMenuOpen(false);
                            setCurrentSubmenu(null);
                            onToggleHelp?.();
                          }}
                        >
                          <span className="menu-item-icon">
                            <FaqIcon
                              width={18}
                              height={18}
                              aria-hidden="true"
                            />
                          </span>
                          <span className="menu-item-label">
                            {t("mobile.faq")}
                          </span>
                        </button>
                      </div>
                    </>
                  )}

                  {/* Language Submenu */}
                  {currentSubmenu === "language" && (
                    <>
                      <div className="submenu-header">
                        <button
                          className="submenu-back"
                          onClick={handleBackFromSubmenu}
                          aria-label={t("common.back")}
                          type="button"
                        >
                          ‹
                        </button>
                        <span className="submenu-title">
                          {t("common.language")}
                        </span>
                      </div>
                      {LANGUAGE_OPTIONS.map((lang) => (
                        <button
                          key={lang.code}
                          className={`toolbar-menu-option${
                            (i18n.resolvedLanguage || i18n.language) ===
                            lang.code
                              ? " is-active"
                              : ""
                          }`}
                          onClick={() => {
                            handleLanguageChange(lang.code);
                            handleBackFromSubmenu();
                          }}
                          type="button"
                        >
                          <span className="option-label">{lang.label}</span>
                          {(i18n.resolvedLanguage || i18n.language) ===
                            lang.code && (
                            <span className="option-check" aria-hidden="true">
                              ✓
                            </span>
                          )}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Toolbar;
